import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router";
import { Shell } from "@/shell/Shell";
import { PageHeader } from "@/shell/PageHeader";
import { frontendErrorsFilters, type FrontendErrorsFilters } from "@/features/observability/components/frontend-errors-filters";
import { frontendErrorsFormat } from "@/features/observability/components/frontend-errors-format";
import { frontendErrorsRange } from "@/features/observability/hooks/frontend-errors-range";
import { frontendErrorsSeriesModel } from "@/features/observability/components/frontend-errors-series-model";
import { FrontendErrorsFilterBar } from "@/features/observability/components/FrontendErrorsFilterBar";
import { FrontendErrorsKpis } from "@/features/observability/components/FrontendErrorsKpis";
import { FrontendErrorsCompleteness } from "@/features/observability/components/FrontendErrorsCompleteness";
import { FrontendErrorsSeriesChart } from "@/features/observability/components/FrontendErrorsSeriesChart";
import { FrontendErrorsGroupsTable } from "@/features/observability/components/FrontendErrorsGroupsTable";
import { FrontendErrorsEventsDrawer } from "@/features/observability/components/FrontendErrorsEventsDrawer";
import {
    useFrontendErrorsEvents,
    useFrontendErrorsGroups,
    useFrontendErrorsSeries,
    useFrontendErrorsSummary,
    type FrontendErrorsStatus,
} from "@/features/observability/hooks/use-frontend-errors";
import type {
    FrontendErrorGroup,
    FrontendErrorsEventsParams,
    FrontendErrorsPopulationParams,
    FrontendErrorsSourceState,
} from "@/features/observability/types/frontend-errors.types";
import "@/features/observability/styles/frontend-errors.css";

const EVENTS_PAGE_SIZE = 50;

const isLowerBound = (state: FrontendErrorsSourceState): boolean => state.sourceStatus !== "ok" || state.countsAreLowerBound;

const isPending = (status: FrontendErrorsStatus): boolean => status === "loading" || status === "idle";

type Selection = { key: string; group: FrontendErrorGroup };

type SectionStateProps = { status: FrontendErrorsStatus; label: string; onRetry: () => void };

function SectionState({ status, label, onRetry }: SectionStateProps) {
    if (isPending(status)) return <div className="fe-section-state">{`Cargando ${label}…`}</div>;
    return (
        <div className="fe-section-state">
            {status === "unavailable" ? `Fuente no disponible: no se puede mostrar ${label}.` : `No se ha podido cargar ${label}.`}
            <button type="button" className="btn btn--sm" onClick={onRetry}>Reintentar</button>
        </div>
    );
}

export function FrontendErrorsPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const filters = useMemo(() => frontendErrorsFilters.parse(searchParams), [searchParams]);
    const [anchor, setAnchor] = useState(() => Date.now());
    const resolved = useMemo(() => frontendErrorsFilters.resolveRange(filters, anchor), [filters, anchor]);
    const range = resolved.range;
    const query = useMemo(() => (range === null ? null : frontendErrorsFilters.toQuery(filters, range)), [filters, range]);

    const summary = useFrontendErrorsSummary(query);
    const asOf = summary.data === null ? null : summary.data.asOf;
    const population = useMemo<FrontendErrorsPopulationParams | null>(
        () => (query === null || asOf === null ? null : { ...query, asOf }),
        [query, asOf],
    );
    const series = useFrontendErrorsSeries(population);
    const groupsQuery = useMemo(() => (population === null ? null : { ...population, includeSample: false }), [population]);
    const groups = useFrontendErrorsGroups(groupsQuery);

    const populationKey = population === null ? null : JSON.stringify(population);
    const [selection, setSelection] = useState<Selection | null>(null);
    const selected = selection !== null && selection.key === populationKey ? selection.group : null;
    const selectedKey = selected === null ? null : frontendErrorsFormat.groupKey(selected);
    // The drawer reads the chosen row only: its fingerprint can also head a row of another category or code.
    const eventsParams = useMemo<FrontendErrorsEventsParams | null>(() => {
        if (population === null || selected === null || selected.fingerprint === null) return null;
        return {
            ...population,
            fingerprint: selected.fingerprint,
            category: frontendErrorsFormat.isCategory(selected.category) ? selected.category : population.category,
            errorCode: selected.errorCode === null ? population.errorCode : selected.errorCode,
            limit: EVENTS_PAGE_SIZE,
        };
    }, [population, selected]);
    const events = useFrontendErrorsEvents(eventsParams);

    const [lastAttemptAt, setLastAttemptAt] = useState<number | null>(null);
    useEffect(() => {
        if (summary.status === "unavailable") setLastAttemptAt(Date.now());
    }, [summary.status]);

    const changeFilters = (next: FrontendErrorsFilters): void => {
        if (next.range !== filters.range || next.customFrom !== filters.customFrom || next.customTo !== filters.customTo) {
            setAnchor(Date.now());
        }
        setSearchParams(frontendErrorsFilters.toSearch(next));
    };

    const reload = (): void => {
        if (filters.range === "custom") {
            summary.refresh();
            return;
        }
        setAnchor(Date.now());
    };

    const openGroup = (group: FrontendErrorGroup): void => {
        if (populationKey !== null) setSelection({ key: populationKey, group });
    };
    const closeDrawer = useCallback((): void => setSelection(null), []);

    const forbidden = [summary.status, series.status, groups.status].some((status) => status === "forbidden");
    const data = summary.data;

    const header = (
        <>
            <PageHeader
                title="Errores del frontend"
                lede="/frontend-errors"
                right={(
                    <button type="button" className="btn" onClick={reload} disabled={isPending(summary.status)}>
                        <span className="material-symbols-outlined">autorenew</span>
                        {isPending(summary.status) ? "Actualizando…" : "Actualizar"}
                    </button>
                )}
            />
            <FrontendErrorsFilterBar
                filters={filters}
                range={range}
                retentionFloor={frontendErrorsRange.retentionFloor(anchor)}
                onChange={changeFilters}
            />
            {range !== null && resolved.clamped ? (
                <div className="fe-notice fe-notice--info" role="status">
                    <span className="material-symbols-outlined">info</span>
                    {`Rango recortado: como máximo 7 días dentro de los últimos 29. Se muestra ${frontendErrorsFormat.dateTime(range.from)} → ${frontendErrorsFormat.dateTime(range.to)}.`}
                </div>
            ) : null}
            {range === null ? (
                <div className="fe-notice fe-notice--info" role="status">
                    <span className="material-symbols-outlined">info</span>
                    {resolved.clamped
                        ? "El rango pedido queda fuera de los 29 días que se conservan. Elige otro rango."
                        : "Elige un rango válido: el inicio debe ser anterior al final."}
                </div>
            ) : null}
        </>
    );

    const body = (): React.ReactNode => {
        if (range === null) return null;
        if (forbidden) {
            return (
                <div className="fe-center">
                    <div className="fe-card fe-message">
                        <span className="material-symbols-outlined fe-message__icon">lock</span>
                        <div className="fe-message__title">Tu usuario no tiene acceso a los errores del frontend</div>
                        <div className="fe-message__text">No es un problema de datos. Pide acceso a quien administre VB Metrics.</div>
                    </div>
                </div>
            );
        }
        if (summary.status === "unavailable") {
            return (
                <div className="fe-stack">
                    <div className="fe-notice fe-notice--crit" role="alert">
                        <span className="material-symbols-outlined">cloud_off</span>
                        {lastAttemptAt === null
                            ? "Fuente no disponible"
                            : `Fuente no disponible · último intento ${frontendErrorsFormat.dateTimeSeconds(lastAttemptAt)}`}
                    </div>
                    <div className="fe-card fe-message">
                        <div className="fe-message__title">No se pueden mostrar errores ahora mismo</div>
                        <div className="fe-message__text">
                            El servicio de ingesta no ha respondido. No hay datos que mostrar para este periodo: esto no significa que no haya habido errores.
                        </div>
                        <button type="button" className="btn" onClick={summary.refresh}>Reintentar</button>
                    </div>
                </div>
            );
        }
        if (summary.status === "error") {
            return (
                <div className="fe-card fe-message">
                    <div className="fe-message__title">No se han podido cargar los errores del frontend</div>
                    <div className="fe-message__text">La respuesta no se ha podido leer. Reintenta o avisa a ingeniería.</div>
                    <button type="button" className="btn" onClick={summary.refresh}>Reintentar</button>
                </div>
            );
        }
        if (data === null) return <div className="fe-section-state">Cargando errores del frontend…</div>;

        const lowerBound = isLowerBound(data);
        const freshness = data.freshness === null ? null : data.freshness.lastReceivedAt;

        return (
            <div className="fe-stack">
                <div className="fe-strip">
                    <span className={`fe-strip__source fe-strip__source--${data.sourceStatus}`}>
                        <span className="fe-strip__dot" />
                        {data.sourceStatus === "ok" ? "Fuente OK" : "Fuente degradada"}
                    </span>
                    <span className="fe-strip__item">
                        <span className="material-symbols-outlined">schedule</span>
                        {frontendErrorsFormat.freshness(freshness, data.asOf)}
                    </span>
                    <span className="fe-muted">{`${frontendErrorsFormat.dateTime(data.from)} → ${frontendErrorsFormat.dateTime(data.to)}`}</span>
                    <span className="fe-muted">Deduplicado en servidor: un fallo = una ocurrencia, aunque se reenvíe</span>
                </div>
                {lowerBound ? (
                    <div className="fe-notice fe-notice--warn" role="status">
                        <span className="material-symbols-outlined">warning</span>
                        Datos parciales: los conteos son un mínimo
                    </div>
                ) : null}
                {data.total === 0 ? (
                    <div className="fe-empty">
                        <div className="fe-card fe-message">
                            <span className="material-symbols-outlined fe-message__icon">inbox</span>
                            <div className="fe-message__title">
                                {`No se han recibido eventos entre ${frontendErrorsFormat.dateTime(data.from)} y ${frontendErrorsFormat.dateTime(data.to)}`}
                            </div>
                            <div className="fe-message__text">
                                Sin eventos no es lo mismo que sin errores. Revisa la completitud: si el navegador declaró huecos, parte de lo ocurrido pudo no llegar.
                            </div>
                        </div>
                        <FrontendErrorsCompleteness completeness={data.completeness} compact />
                    </div>
                ) : (
                    <>
                        <FrontendErrorsKpis summary={data} lowerBound={lowerBound} />
                        <div className="fe-row">
                            <div className="fe-card fe-row__main">
                                {series.data !== null && series.data.buckets !== null ? (
                                    <FrontendErrorsSeriesChart
                                        model={frontendErrorsSeriesModel.toModel(series.data.buckets, data.completeness)}
                                        bucketMs={series.data.bucketMs}
                                        rangeMs={data.to - data.from}
                                        lowerBound={isLowerBound(series.data)}
                                    />
                                ) : null}
                                {series.data !== null && series.data.buckets === null ? (
                                    <div className="fe-section-state">La fuente no ha devuelto la serie para este periodo.</div>
                                ) : null}
                                {series.data === null ? <SectionState status={series.status} label="la serie" onRetry={series.refresh} /> : null}
                            </div>
                            <FrontendErrorsCompleteness completeness={data.completeness} compact={false} />
                        </div>
                        {groups.data !== null && groups.data.groups !== null ? (
                            <FrontendErrorsGroupsTable
                                groups={groups.data.groups}
                                asOf={groups.data.asOf}
                                lowerBound={isLowerBound(groups.data)}
                                truncated={groups.data.reason === "groups_truncated"}
                                selectedKey={selectedKey}
                                onOpen={openGroup}
                            />
                        ) : null}
                        {groups.data !== null && groups.data.groups === null ? (
                            <div className="fe-card fe-section-state">La fuente no ha devuelto los grupos para este periodo.</div>
                        ) : null}
                        {groups.data === null ? (
                            <div className="fe-card">
                                <SectionState status={groups.status} label="los grupos" onRetry={groups.refresh} />
                            </div>
                        ) : null}
                    </>
                )}
            </div>
        );
    };

    return (
        <Shell title="Errores del frontend">
            <div className="frontend-errors">
                {header}
                {body()}
                {selected !== null && !forbidden ? (
                    <FrontendErrorsEventsDrawer group={selected} resource={events} onClose={closeDrawer} />
                ) : null}
            </div>
        </Shell>
    );
}
